import { authService } from "@/services/AuthService";
import { EmojiPicker } from "./EmojiPicker";
/**
 * 评论发布器 —— 评论表单组件
 *
 * 职责：
 * 1. 初始化表单内的 Emoji 选择器
 * 2. 未登录时拦截提交，执行登录 + 评论
 * 3. 已登录时由 Unpoly 处理提交
 */

export interface Options {
  target: string;
  emptyHintContainer: string;
  selector: string;
  emojiPickerSelector?: string;
}
export class Publisher {
  private options: Options;
  private emojiPicker: EmojiPicker | null = null;
  private formEl: HTMLFormElement;

  constructor(element: HTMLFormElement, options: Options) {
    console.info("[Publisher] 正在初始化", { element, options });
    this.formEl = element;
    this.options = options;
    const emojiSelector = options.emojiPickerSelector || ".emoji-picker";
    const emojiContainer = element.querySelector<HTMLElement>(emojiSelector);
    if (emojiContainer) {
      this.emojiPicker = new EmojiPicker(
        emojiContainer,
        element.querySelector("textarea")!,
      );
    }
    up.on(element, "up:form:submit", this.submit);
  }

  submit = (event: up.FormSubmitEvent) => {
    console.info("[Publisher] 拦截到提交事件", {
      isSignedIn: authService.isSignedIn,
    });

    if (authService.isSignedIn) {
      // 已登录：仅注入配置，让 Unpoly 继续默认的提交动作
      const { target, emptyHintContainer } = this.options;
      console.info("[Publisher] 应用常规渲染配置", { target });
      event.renderOptions.target = `${target}:before`; // 指定渲染目标为评论列表
      event.renderOptions.onFinished = () => this.onSuccess(emptyHintContainer);
      return;
    }

    // 未登录：真正拦截提交，进入自定义流程
    event.preventDefault();
    console.info("[Publisher] 用户未登录，触发登录流程");
    // 弹出登录 Modal
    authService.signin({
      onAccepted: () => {
        console.info("[Publisher] 登录成功，执行评论提交");
        this.perform();
      },
    });
  };

  /**
   * 登录完成后：直接使用 up.render 发送请求
   * 这样可以绕过事件拦截系统，语义也更清晰
   */
  private perform(): void {
    const { target, emptyHintContainer } = this.options;
    console.info("[Publisher] 正在调用 up.render 发送评论", {
      action: this.formEl.action,
    });
    up.render({
      url: this.formEl.action,
      method: this.formEl.method,
      params: up.Params.fromForm(this.formEl),
      target: `${target}:before`,
      layer: "root",
      onFinished: () => this.onSuccess(emptyHintContainer),
    });
  }

  /**
   * 统一处理提交成功后的清理工作
   */
  private onSuccess(emptyHintContainer: string): void {
    console.info("[Publisher] 评论发布成功，正在清理表单");
    document.querySelector(emptyHintContainer)?.remove(); // 移除"暂无评论"提示
    this.formEl.reset(); // 提交成功后重置表单
  }

  destroy(): void {
    console.info("[Publisher] 正在销毁组件");
    this.emojiPicker?.destroy();
    if (this.formEl && this.submit) {
      up.off(this.formEl, "up:form:submit", this.submit); // Unpoly 事件解绑需要用 up.off
    }
  }

  /** 注册 Unpoly 编译器 */
  static compiler(selector: string, options: Options): void {
    up.compiler(selector, (element: Element) => {
      const form = new Publisher(element as HTMLFormElement, options);
      return () => form.destroy();
    });
  }
}
